import CardLayout from '@/components/common/CardLayout';
import Modal from '@/components/common/modals/Modal';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { useMutation } from '@apollo/client';
import { assert } from '@common/errors';
import { ExternalFinancialEntity } from '@common/financialEntities';
import { UpdateFinancialEntityTypeDocument } from '@common/graphql/generated';
import {
    configByFECombinedType,
    FinancialEntityCombinedType,
} from '@common/react/financialEntities';
import { useState } from 'react';

export interface FinancialEntityTypeChangeModalProps {
    fe: ExternalFinancialEntity;
    newFECombinedType: FinancialEntityCombinedType;
}

export const showFinancialEntityTypeChangeModal = (
    props: FinancialEntityTypeChangeModalProps
) => ModalManager.show(FinancialEntityTypeChangeModal, props);

const FinancialEntityTypeChangeModal =
    ModalManager.create<FinancialEntityTypeChangeModalProps>(
        ({ fe, newFECombinedType }) => {
            useTrackModalShown('Financial Entity Type Change', {
                type: newFECombinedType,
            });

            const modal = useModal();

            const [updateFinancialEntityType] = useMutation(
                UpdateFinancialEntityTypeDocument
            );
            const [isSubmitting, setIsSubmitting] = useState(false);

            const updateCombinedType = (
                combinedType: FinancialEntityCombinedType
            ) => {
                const feCombinedTypeConfig =
                    configByFECombinedType[combinedType];
                assert(feCombinedTypeConfig);
                const { type, subtype } = feCombinedTypeConfig;
                setIsSubmitting(true);
                updateFinancialEntityType({
                    variables: {
                        financialEntityId: fe.id,
                        financialEntityType: type,
                        financialEntitySubtype: subtype,
                    },
                })
                    .then(() => {
                        modal.hide();
                    })
                    .finally(() => {
                        setIsSubmitting(false);
                    });
            };

            return (
                <Modal size="sm" isOpen={modal.visible} onClose={modal.hide}>
                    <CardLayout
                        title={`Are you sure you want to change the account type?`}
                        primaryButtonText="Change account type"
                        onPrimaryButton={() => {
                            updateCombinedType(newFECombinedType);
                        }}
                        isPrimaryButtonLoading={isSubmitting}
                        onSecondaryButton={modal.hide}
                        onClickPrimaryEvent="Financial Entity Type Change Modal - Confirm Change Account Type Button Clicked"
                        onClickPrimaryEventParams={{ type: newFECombinedType }}
                        onClickSecondaryEvent="Financial Entity Type Change Modal - Cancel Button Clicked"
                    ></CardLayout>
                </Modal>
            );
        }
    );

export default FinancialEntityTypeChangeModal;
